import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from './utils/api';

const AUTOMATION_PLAN = [
  {
    key: 'commercial-build',
    weekday: 1,
    time: '09:00',
    title: 'Commercial newsletter build',
    audience: 'commercial',
    type: 'newsletter',
    action: 'build',
    description: 'Hermes picks an approved commercial topic, creates the campaign with commercial template, generates content, and sends a test email for review.',
  },
  {
    key: 'commercial-send',
    weekday: 2,
    time: '08:30',
    title: 'Commercial newsletter send',
    audience: 'commercial',
    type: 'newsletter',
    action: 'send',
    description: 'Sends the approved commercial newsletter to all commercial contacts.',
  },
  {
    key: 'residential-build',
    weekday: 3,
    time: '09:00',
    title: 'Residential newsletter build',
    audience: 'domestic',
    type: 'newsletter',
    action: 'build',
    description: 'Hermes picks an approved domestic topic, creates the campaign with domestic template, generates content, and sends a test email for review.',
  },
  {
    key: 'residential-send',
    weekday: 4,
    time: '08:30',
    title: 'Residential newsletter send',
    audience: 'domestic',
    type: 'newsletter',
    action: 'send',
    description: 'Sends the approved residential newsletter to all domestic contacts.',
  },
  {
    key: 'blog-post',
    weekday: 5,
    time: '10:00',
    title: 'Website blog draft',
    audience: 'website',
    type: 'blog',
    action: 'build',
    description: 'Hermes creates a blog draft with a selected featured image, ready for approval and WordPress publishing.',
  },
];

const STATUS_STYLES = {
  scheduled: 'bg-blue-50 text-blue-800 border-blue-200',
  generating: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  awaiting_approval: 'bg-orange-50 text-orange-800 border-orange-200',
  approved: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  sent: 'bg-green-50 text-green-800 border-green-200',
  completed: 'bg-green-50 text-green-800 border-green-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  rejected: 'bg-red-50 text-red-800 border-red-200',
  planned: 'bg-slate-50 text-slate-700 border-slate-200 border-dashed',
};

const STATUS_DOTS = {
  scheduled: '🔵',
  generating: '🟡',
  awaiting_approval: '🟠',
  approved: '🔷',
  sent: '🟢',
  completed: '🟢',
  failed: '🔴',
  rejected: '🔴',
  planned: '⚪',
};

const TYPE_LABELS = {
  newsletter: 'Newsletter',
  blog: 'Blog',
};

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function monthKey(date) {
  return date.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function audienceValue(item) {
  return String(item?.audience_category || item?.variable_slots?.audience || item?.audience || '').toLowerCase();
}

function audienceLabel(item) {
  const audience = audienceValue(item);
  if (audience === 'domestic') return 'Domestic';
  if (audience === 'commercial') return 'Commercial';
  if (audience === 'website') return 'Website';
  return 'Unspecified';
}

function statusLabel(status) {
  switch (status) {
    case 'scheduled': return 'Scheduled';
    case 'generating': return 'Generating';
    case 'awaiting_approval': return 'Awaiting Approval';
    case 'approved': return 'Approved';
    case 'sent': return 'Sent';
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    case 'rejected': return 'Rejected';
    case 'planned': return 'Planned Slot';
    default: return status || 'Unknown';
  }
}

function statusDot(status) {
  return STATUS_DOTS[status] || STATUS_DOTS.planned;
}

function plannedDateFor(dayDate, plan) {
  const [hours, minutes] = plan.time.split(':').map(Number);
  return new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), hours, minutes);
}

function buildPlannedItems(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const items = [];

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    for (const plan of AUTOMATION_PLAN) {
      if (date.getDay() === plan.weekday) {
        const dueAt = plannedDateFor(date, plan);
        items.push({
          id: `${plan.key}-${dueAt.toISOString()}`,
          kind: 'planned',
          status: 'planned',
          name: plan.title,
          type: plan.type,
          audience: plan.audience,
          due_at: dueAt.toISOString(),
          description: plan.description,
        });
      }
    }
  }

  return items;
}

function CampaignCalendar() {
  const [campaigns, setCampaigns] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    try {
      const res = await apiFetch('/api/marketing/campaigns');
      if (!res.ok) throw new Error(`Campaign request failed: ${res.status}`);
      const data = await res.json();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const plannedItems = useMemo(() => buildPlannedItems(year, month), [year, month]);

  const itemsByDay = useMemo(() => {
    const grouped = {};

    for (const campaign of campaigns) {
      if (!campaign.due_at) continue;
      const dueDate = new Date(campaign.due_at);
      if (Number.isNaN(dueDate.getTime())) continue;
      if (dueDate.getFullYear() !== year || dueDate.getMonth() !== month) continue;
      const day = dueDate.getDate();
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push({ ...campaign, kind: 'campaign' });
    }

    for (const item of plannedItems) {
      const dueDate = new Date(item.due_at);
      const day = dueDate.getDate();
      const hasCampaignSameDay = (grouped[day] || []).some(campaign => {
        const campaignDate = new Date(campaign.due_at);
        return sameDay(campaignDate, dueDate)
          && campaign.type === item.type
          && audienceValue(campaign) === item.audience;
      });
      if (hasCampaignSameDay) continue;

      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(item);
    }

    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    });

    return grouped;
  }, [campaigns, month, plannedItems, year]);

  const stats = useMemo(() => ({
    awaiting: campaigns.filter(c => c.status === 'awaiting_approval').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length,
    approved: campaigns.filter(c => c.status === 'approved').length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    failed: campaigns.filter(c => ['failed', 'rejected'].includes(c.status)).length,
  }), [campaigns]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) {
    cells.push(<div key={`empty-${i}`} className="min-h-[112px] bg-gray-50 border border-gray-100" />);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dayDate = new Date(year, month, day);
    const isToday = sameDay(today, dayDate);
    const dayItems = itemsByDay[day] || [];

    cells.push(
      <div
        key={day}
        className={`min-h-[112px] border p-2 transition-colors ${
          isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
        }`}
      >
        <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-blue-700' : 'text-gray-500'}`}>
          {day}
        </div>
        <div className="space-y-1">
          {dayItems.slice(0, 3).map(item => (
            <button
              key={item.id}
              onClick={() => setSelectedItem(item)}
              className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded border truncate ${STATUS_STYLES[item.status] || STATUS_STYLES.planned}`}
              title={item.name}
            >
              <span className="font-semibold">{new Date(item.due_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
              {' '}
              {item.action === 'build' ? '🔧' : item.action === 'send' ? '✉️' : statusDot(item.status)}
              {' '}
              {item.name}
            </button>
          ))}
          {dayItems.length > 3 && (
            <div className="text-[10px] text-gray-400 pl-1">+{dayItems.length - 3} more</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Marketing Calendar</h2>
          <p className="text-xs text-gray-500">Weekly automation plan plus live campaigns awaiting approval, sending or publishing.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="px-2 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200">Prev</button>
          <button onClick={goToday} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium">{monthKey(currentDate)}</button>
          <button onClick={nextMonth} className="px-2 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200">Next</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-3 border-b bg-gray-50 text-xs">
        <div className="rounded border bg-white px-3 py-2"><span className="font-semibold text-orange-700">{stats.awaiting}</span> awaiting approval</div>
        <div className="rounded border bg-white px-3 py-2"><span className="font-semibold text-blue-700">{stats.scheduled}</span> scheduled</div>
        <div className="rounded border bg-white px-3 py-2"><span className="font-semibold text-indigo-700">{stats.approved}</span> approved</div>
        <div className="rounded border bg-white px-3 py-2"><span className="font-semibold text-green-700">{stats.sent}</span> sent</div>
        <div className="rounded border bg-white px-3 py-2"><span className="font-semibold text-red-700">{stats.failed}</span> needs attention</div>
      </div>

      <div className="px-4 py-3 border-b">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {AUTOMATION_PLAN.map(plan => (
            <div key={plan.key} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs font-semibold text-slate-800">{plan.title}</div>
              <div className="text-[11px] text-slate-500">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][plan.weekday]} at {plan.time}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-7">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2 border-b bg-gray-50">{day}</div>
        ))}
        {cells}
      </div>

      {selectedItem && (
        <div className="border-t p-4 bg-gray-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_STYLES[selectedItem.status] || STATUS_STYLES.planned}`}>
                  {statusDot(selectedItem.status)} {statusLabel(selectedItem.status)}
                </span>
                <h3 className="font-semibold text-gray-800">{selectedItem.name}</h3>
              </div>
              <div className="mt-2 space-y-1 text-sm text-gray-600">
                <p>Due: {formatDateTime(selectedItem.due_at)}</p>
                <p>Audience: {audienceLabel(selectedItem)}</p>
                <p>Type: {TYPE_LABELS[selectedItem.type] || selectedItem.type || 'Campaign'}</p>
                {selectedItem.sent_count != null && <p>Sent to: {selectedItem.sent_count} contacts</p>}
                {selectedItem.error_count > 0 && <p className="text-red-600">Errors: {selectedItem.error_count}</p>}
                {selectedItem.kind === 'planned' && <p>{selectedItem.description}</p>}
                {selectedItem.kind === 'campaign' && selectedItem.status === 'awaiting_approval' && (
                  <p className="text-orange-700 font-medium">This item is ready for review in Campaigns.</p>
                )}
                {selectedItem.last_run_at && <p>Last run: {formatDateTime(selectedItem.last_run_at)}</p>}
                {selectedItem.sent_at && <p>Sent at: {formatDateTime(selectedItem.sent_at)}</p>}
                {selectedItem.next_run_at && <p>Next: {formatDateTime(selectedItem.next_run_at)}</p>}
                {selectedItem.failed_at && <p>Failed: {formatDateTime(selectedItem.failed_at)}</p>}
                {selectedItem.failure_reason && <p className="text-red-600">Reason: {selectedItem.failure_reason}</p>}
              </div>
            </div>
            <button onClick={() => setSelectedItem(null)} className="text-gray-400 hover:text-gray-600 text-lg">x</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="p-8 text-center text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto mb-2" />
          Loading campaigns...
        </div>
      )}
    </div>
  );
}

export default CampaignCalendar;
